import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { STATE_META } from "@/lib/constants";
import { PRODUCT_CATALOG } from "@/lib/productCatalog";
import { menusFor, activeCount, firstLink } from "@/components/AdminNav";
import { ArrowRight, Bell, Clock, FileCheck2, Wallet, AlertTriangle, ShoppingBag } from "lucide-react";

function Kpi({ label, value, sub, tid }) {
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid={`kpi-${tid}`}>
      <div className="font-heading text-3xl font-semibold text-forest">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground/80">{sub}</div>}
    </div>
  );
}

function AreaCard({ menu }) {
  const to = firstLink(menu);
  return (
    <Link to={to || "/admin"} data-testid={`area-card-${menu.key}`} className="group flex flex-col rounded-2xl border bg-white p-6 transition-shadow hover:shadow-xl">
      <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: `${menu.color}1A` }}><menu.icon className="h-5 w-5" style={{ color: menu.color }} /></div>
      <div className="mt-4 flex items-center gap-2 font-heading text-lg font-semibold text-forest">{menu.label}
        <span className="rounded-md bg-sage px-1.5 text-[11px] font-semibold text-forest">{activeCount(menu)}</span></div>
      <p className="mt-1 flex-1 text-sm text-muted-foreground">{menu.desc}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors group-hover:text-terracotta-hover">Apri <ArrowRight className="h-4 w-4" /></span>
    </Link>
  );
}

function Promemoria({ items }) {
  const shown = items.filter((i) => i.n > 0);
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid="promemoria-agenzia">
      <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"><Bell className="h-4 w-4" />Promemoria Agenzia</div>
      {shown.length === 0 && <p className="text-sm text-muted-foreground" data-testid="promemoria-empty">Nessuna attività in sospeso.</p>}
      <div className="space-y-2">
        {shown.map((i) => (
          <Link key={i.key} to={i.to} data-testid={`promemoria-${i.key}`} className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-forest transition-colors hover:brightness-95 ${i.bg}`}>
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${i.dot}`} /><i.icon className="h-4 w-4 shrink-0" /><span className="flex-1"><b>{i.n}</b> {i.text}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Recent({ rows }) {
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid="attivita-recente">
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-forest"><Clock className="h-4 w-4" />Attività recente</span>
        <Link to="/admin/preventivi" className="text-xs text-muted-foreground hover:text-forest">tutte le pratiche</Link>
      </div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">Nessuna pratica.</p>}
      <div className="divide-y">
        {rows.map((q) => (
          <Link key={q.id} to={`/admin/preventivi/${q.id}`} data-testid={`recent-${q.id}`} className="block py-2.5 hover:bg-sage/50">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span className="rounded bg-sage px-1.5 py-0.5 font-semibold text-forest">{q.codice_prev || "Pratica"}</span>
              <span>{q.created_at ? new Date(q.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : ""}</span>
            </div>
            <div className="mt-1 truncate text-sm font-medium text-forest">{q.cliente_nome}</div>
            <div className="text-xs text-muted-foreground">{PRODUCT_CATALOG[q.product_code]?.label || q.product_code} · {STATE_META[q.stato]?.label || q.stato}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [adesioni, setAdesioni] = useState(0);
  const [inviate, setInviate] = useState(0);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/admin/kpi").then(({ data }) => setKpi(data));
    api.get("/admin/adesioni/count").then(({ data }) => setAdesioni(data.count || 0)).catch(() => {});
    api.get("/admin/trattative", { params: { stato: "inviata" } }).then(({ data }) => setInviate(data.length)).catch(() => {});
    api.get("/admin/quotes", { params: { limit: 6 } }).then(({ data }) => setRecent(data)).catch(() => {});
  }, []);
  if (!kpi) return null;
  const total = kpi.standard + kpi.referral || 1;
  const stdPct = Math.round((kpi.standard / total) * 100);
  const promemoria = [
    { key: "adesioni", n: adesioni, text: "adesioni da verificare", to: "/admin/adesioni", icon: FileCheck2, bg: "bg-red-50", dot: "bg-red-500" },
    { key: "bonifici", n: kpi.incassi_pending, text: "bonifici da confermare", to: "/admin/preventivi?stato=in_attesa_bonifico", icon: Wallet, bg: "bg-amber-50", dot: "bg-amber-500" },
    { key: "referral", n: kpi.referrals_open, text: "referral da valutare", to: "/admin/preventivi?stato=referral_inviato", icon: AlertTriangle, bg: "bg-amber-50", dot: "bg-amber-500" },
    { key: "proposte", n: inviate, text: "proposte uniche in attesa del cliente", to: "/admin/trattative", icon: ShoppingBag, bg: "bg-sage", dot: "bg-forest" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Buongiorno{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
        <p className="mt-1 text-muted-foreground">Governance della rete Partner e delle pratiche, per tutta l'Agenzia.</p>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi tid="partner-attivi" label="Partner attivi" value={kpi.total_partners} />
        <Kpi tid="richieste" label="Richieste" value={kpi.total_requests} />
        <Kpi tid="preventivi" label="Preventivi" value={kpi.total_quotes} sub={`${kpi.referrals_open} referral aperti`} />
        <Kpi tid="polizze" label="Polizze" value={kpi.polizze} sub={`${kpi.incassi_pending} incassi da confermare`} />
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Le tue aree</div>
          <div className="grid gap-5 sm:grid-cols-2">{menusFor(user?.role).map((m) => <AreaCard key={m.key} menu={m} />)}</div>
        </div>
        <div className="space-y-6"><Promemoria items={promemoria} /><Recent rows={recent} /></div>
      </div>
      <div className="rounded-2xl border bg-white p-5" data-testid="pratiche-per-stato">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-forest">Pratiche per stato</h2>
          <span className="text-xs text-muted-foreground" data-testid="kpi-standard-vs-referral">Standard {stdPct}% · Referral {kpi.referral}</span>
        </div>
        <div className="space-y-2">
          {Object.entries(kpi.by_state).map(([state, count]) => (
            <div key={state} className="flex items-center gap-3">
              <span className="w-44 text-sm text-forest">{(STATE_META[state] || { label: state }).label}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-sage"><div className="h-full rounded-full bg-forest" style={{ width: `${Math.round((count / (kpi.total_quotes || 1)) * 100)}%` }} /></div>
              <span className="w-8 text-right text-sm font-medium text-forest">{count}</span>
            </div>
          ))}
          {Object.keys(kpi.by_state).length === 0 && <p className="text-sm text-muted-foreground">Nessuna pratica.</p>}
        </div>
      </div>
    </div>
  );
}
